import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { forEach as _forEach, get as _get, random as _random } from 'lodash';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { PaymentAmountModel, PaymentAmountRadioState } from '@amfam/shared/models';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { ValidationService } from '@amfam/ui-kit';

@Component({
  selector: 'ds-payment-radio-selection',
  templateUrl: './payment-radio-selection.component.html',
  styleUrls: ['./payment-radio-selection.component.scss'],
  standalone: false
})
export class PaymentRadioSelectionComponent implements OnInit, OnDestroy {
  @Input() paymentAmounts: PaymentAmountModel;
  @Input() showBillAccountName: boolean;
  @Input() showOtherAmount = true;
  @Input() directionFlow: string;
  @Input() radioAmount = true;
  @Input() allowRemove = true;
  @Input() showZeroAmounts = false;
  @Input() preserveOtherAmountValue = false;
  @Input() paymentAmountsCount: number;
  @Input() paymentType = 'minAmountDue';

  @Output() valueSelect = new EventEmitter();
  @Output() removeSelect = new EventEmitter();

  @ViewChild('radioBoxInput') private radioBoxInput: ElementRef;

  paymentAmountCtrl: AbstractControl;
  uniqueId = '';
  rowAmountSelectionForm: UntypedFormGroup;
  amtValidationError = '';

  private controlElement: HTMLInputElement;
  private otherAmountCtrl: AbstractControl;
  private validationErrorMsg: {
    invalidPaymentAmnt: string;
    paymentGreaterThanZero: string;
    noMoreThanPersonalLimit: string;
    noMoreThanCommercialLimit: string;
    noMoreThanTenOverBalance: string;
  };

  private otherAmountStore = 0;
  private currentPaymentAmtSelection = 0;

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private copyService: CopyService
  ) {
    this.validationErrorMsg = this.copyService.getCopy('billing.validations');
  }

  ngOnInit() {
    this.uniqueId = this.paymentAmounts.accNum + '-' + _random(9999);
    this.setUpForm();
    this.setUpRadioState(this.paymentAmounts.editAutoPayAmount);
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  onOtherAmtClick(paymentType: string, event?: unknown) {
    // JC: Only trigger this if we have a payment type, it has changed and otherAmountCtrl exists
    if (
      paymentType &&
      paymentType !== this.paymentType &&
      this.otherAmountCtrl &&
      this.paymentAmounts
    ) {
      // First thing, update the payment type so we can filter in the onchange listeners
      this.paymentType = paymentType;
      // JC: Add validators back in case they needed to be cleared by mindue or unpaid selection
      this.otherAmountCtrl.setValidators(
        Validators.compose([
          ValidationService.paymentAmountValidation(false),
          ValidationService.paymentAmountMaxValidation(this.paymentAmounts)
        ])
      );
      this.otherAmountCtrl.updateValueAndValidity();

      // AS : If the user clicks on the input field directly clear the radio buttons.
      this.paymentAmountCtrl.setValue('');
      this.currentPaymentAmtSelection = 0;
      // JC : If a paymentAmount was passed in that didn't match minDue or unpaidBalance,
      //      this will reset the value to the paymentAmount that we stored in setUpRadioState()
      //      Maybe we don't want this, but I liked it. TODO: discuss
      //      If otherAmountStore value is 0, change it to match the min amount due opposed to 0
      // SK: Optionally allow original otherAmount value to be preserved and restored.
      if (this.preserveOtherAmountValue) {
        if (this.otherAmountStore) {
          this.otherAmountCtrl.setValue(this.otherAmountStore);
        } else if (this.paymentAmounts.minDue) {
          this.otherAmountCtrl.setValue(this.paymentAmounts.minDue);
        }
        this.otherAmountCtrl.updateValueAndValidity();
      }

      if (event && this.radioBoxInput) {
        this.controlElement = this.radioBoxInput.nativeElement.querySelector('input');
        if (this.controlElement) {
          this.controlElement.focus();
        }
      }

      // SK: if other amount is not available? set amountSelected to 0 and error: INVALID to not allow further flow.
      if (!this.otherAmountCtrl.value) {
        this.valueSelect.emit({
          amountSelected: 0,
          accNum: this.paymentAmounts.accNum,
          error: 'INVALID',
          paymentType: this.paymentType
        });
      }
    }
  }

  onRemoveClick() {
    this.removeSelect.emit({ accNum: this.paymentAmounts.accNum });
  }

  onRadioAmtClick(paymentType: string, clearOther = true) {
    // JC: Only trigger this if there's a change of paymentType
    if (paymentType && paymentType !== this.paymentType) {
      // First thing, update the payment type so we can filter in the onchange listeners
      this.paymentType = paymentType;

      if (paymentType === PaymentAmountRadioState.MINIMUM_DUE) {
        this.currentPaymentAmtSelection = this.paymentAmounts.minDue;
        this.paymentAmountCtrl.setValue(PaymentAmountRadioState.MINIMUM_DUE);
      } else if (paymentType === PaymentAmountRadioState.PAST_PLUS_CURRENT) {
        this.currentPaymentAmtSelection =
          this.paymentAmounts.pastDuePaymentInfo.currentDueAmount +
          this.paymentAmounts.pastDuePaymentInfo.pastDueAmount;
        this.paymentAmountCtrl.setValue(PaymentAmountRadioState.PAST_PLUS_CURRENT);
      } else if (paymentType === PaymentAmountRadioState.PAST_DUE) {
        this.currentPaymentAmtSelection = this.paymentAmounts.pastDuePaymentInfo.pastDueAmount;
        this.paymentAmountCtrl.setValue(PaymentAmountRadioState.PAST_DUE);
      } else {
        this.currentPaymentAmtSelection = this.paymentAmounts.balance;
        this.paymentAmountCtrl.setValue('accountBalance');
      }

      // JC : Allow override of otherAmountCtrl clear, so we can use the same function
      //      for both the (click)event and in setUpRadioState()
      //      clearOther is optional, if not provided it will use the the default value of true
      if (clearOther) {
        this.clearAmtValidationErrors();
        this.otherAmountCtrl.setValue('');
        this.otherAmountCtrl.updateValueAndValidity();
      }
    }
  }

  /*
    Pass boolean to paymentAmountValidation to return correct message based on whether billing
    codes is enabled or not.
  */
  private updateValidators(paymentType: string) {
    if (paymentType !== 'otherAmount') {
      this.rowAmountSelectionForm.clearValidators();
    } else {
      this.otherAmountCtrl.setValidators(
        Validators.compose([
          ValidationService.paymentAmountValidation(true),
          ValidationService.paymentAmountMaxValidation(this.paymentAmounts)
        ])
      );
    }
    this.otherAmountCtrl.updateValueAndValidity();
  }

  private hasAmtValidationError() {
    this.amtValidationError = '';
    let amtValidationError = '';

    // These are the validations we'll check for in the form controls error object
    const validationNameArray = [
      'invalidPaymentAmnt',
      'paymentGreaterThanZero',
      'noMoreThanPersonalLimit',
      'noMoreThanCommercialLimit',
      'noMoreThanTenOverBalance'
    ];

    _forEach(validationNameArray, validationName => {
      if (this.otherAmountCtrl.hasError(validationName)) {
        // If there's a match, set the property to the string for this error in the copy service
        amtValidationError = this.validationErrorMsg[validationName];
        // break out of the forEach
        return;
      }
    });
    return amtValidationError;
  }

  private clearAmtValidationErrors() {
    this.amtValidationError = '';
    this.otherAmountCtrl.markAsPristine();
    this.otherAmountCtrl.clearValidators();
    this.otherAmountCtrl.updateValueAndValidity();
  }

  private setUpForm() {
    this.rowAmountSelectionForm = this.formBuilder.group({
      paymentAmountCtrl: [''],
      otherAmountCtrl: [
        '',
        Validators.compose([
          ValidationService.paymentAmountValidation(false),
          ValidationService.paymentAmountMaxValidation(this.paymentAmounts)
        ])
      ]
    });

    this.otherAmountCtrl = this.rowAmountSelectionForm.controls['otherAmountCtrl'];
    this.paymentAmountCtrl = this.rowAmountSelectionForm.controls['paymentAmountCtrl'];

    this.paymentAmountCtrl.valueChanges
      .pipe(takeUntil(this.stop$), distinctUntilChanged())
      .subscribe(() => {
        if (this.paymentType !== 'otherAmount') {
          this.updateValidators(this.paymentType);
          this.updateValidators('otherAmount');
          this.valueSelect.emit({
            amountSelected: this.currentPaymentAmtSelection || 0,
            accNum: this.paymentAmounts.accNum,
            error: 'VALID', // SK: set to VALID state if paymentType is not otherAmount
            paymentType: this.paymentType
          });
        } else {
          this.updateValidators('otherAmount');
        }
      });

    this.rowAmountSelectionForm.statusChanges
      .pipe(takeUntil(this.stop$), distinctUntilChanged())
      .subscribe(formValues => {
        if (formValues === 'INVALID') {
          this.valueSelect.emit({
            amountSelected: 0,
            accNum: this.paymentAmounts.accNum,
            error: this.rowAmountSelectionForm.status,
            paymentType: this.paymentType
          });
        } else if (this.paymentType === 'otherAmount') {
          this.valueSelect.emit({
            amountSelected: this.otherAmountCtrl.value,
            accNum: this.paymentAmounts.accNum,
            error: this.rowAmountSelectionForm.status,
            paymentType: this.paymentType
          });
        }
      });

    this.otherAmountCtrl.valueChanges
      .pipe(takeUntil(this.stop$), distinctUntilChanged())
      .subscribe(formValues => {
        if (formValues || this.paymentType === 'otherAmount') {
          // Emit only if values present or we are editing other amount
          this.valueSelect.emit({
            amountSelected: formValues || 0,
            accNum: this.paymentAmounts.accNum,
            error: this.rowAmountSelectionForm.status,
            paymentType: this.paymentType
          });
          if (this.otherAmountCtrl.dirty) {
            this.amtValidationError = this.hasAmtValidationError();
          }
        }
      });
  }

  // JC: Check to see if a paymentAmount was passed in and check radio values accordingly
  private setUpRadioState(preSelectedRadioState?: string) {
    /**
     * @description If we are editing a autopay we already know the radio button state to show, skip the
     * computation of the radio button.
     */
    if (preSelectedRadioState) {
      this.onRadioAmtClick(preSelectedRadioState, true);
    } else {
      // If a selectedAmount was set, initialize the radio buttons to the selectedAmount
      if (this.paymentAmounts && this.paymentAmounts.selectedAmount) {
        // Handle pre-selected non-past due
        if (!this.paymentAmounts.pastDuePaymentInfo) {
          switch (this.paymentAmounts.selectedAmount) {
            // If paymentAmount matches minDue. Check it
            case Number(this.paymentAmounts.minDue):
              this.onRadioAmtClick(PaymentAmountRadioState.MINIMUM_DUE, true);
              break;

            // If it matches the unpaidBalance. Check it
            case Number(this.paymentAmounts.balance):
              this.onRadioAmtClick(PaymentAmountRadioState.UNPAID_BALANCE, true);
              break;

            // If it doesn't match either of those, it must be other, Check it
            default:
              this.otherAmountStore = this.paymentAmounts.selectedAmount;
              this.onOtherAmtClick(PaymentAmountRadioState.OTHER_AMOUNT, null);
              break;
          }
        } else {
          switch (this.paymentAmounts.selectedAmount) {
            // If paymentAmount matches minDue. Check it
            case Number(_get(this.paymentAmounts, 'pastDuePaymentInfo.pastDueAmount')):
              this.onRadioAmtClick(PaymentAmountRadioState.PAST_DUE, true);
              break;

            // If paymentAmount matches minDue. Check it
            case Number(
              _get(this.paymentAmounts, 'pastDuePaymentInfo.pastDueAmount') +
                _get(this.paymentAmounts, 'pastDuePaymentInfo.currentDueAmount')
            ):
              this.onRadioAmtClick(PaymentAmountRadioState.PAST_PLUS_CURRENT, true);
              break;

            // If it matches the unpaidBalance. Check it
            case Number(_get(this.paymentAmounts, 'balance')):
              this.onRadioAmtClick(PaymentAmountRadioState.UNPAID_BALANCE, true);
              break;

            // If it doesn't match either of those, it must be other, Check it
            default:
              this.otherAmountStore = _get(
                this.paymentAmounts,
                'pastDuePaymentInfo.selectedAmount'
              );
              this.onOtherAmtClick(PaymentAmountRadioState.OTHER_AMOUNT, null);
              break;
          }
        }
      } else if (
        this.paymentAmounts &&
        this.paymentAmounts.minDue <= 0 &&
        this.paymentAmounts.balance <= 0
      ) {
        this.updateValidators(PaymentAmountRadioState.OTHER_AMOUNT);
      } else if (this.paymentAmounts && this.paymentAmounts.pastDuePaymentInfo) {
        if (
          this.paymentAmounts.pastDuePaymentInfo.cancelationLetterSent &&
          this.paymentAmounts.pastDuePaymentInfo.currentDueAmount !== 0
        ) {
          this.onRadioAmtClick(PaymentAmountRadioState.PAST_PLUS_CURRENT);
        } else {
          this.onRadioAmtClick(PaymentAmountRadioState.PAST_DUE);
        }
      } else {
        if (!this.showZeroAmounts && this.paymentAmounts && this.paymentAmounts.minDue <= 0) {
          this.onOtherAmtClick(PaymentAmountRadioState.OTHER_AMOUNT, null);
        } else {
          this.onRadioAmtClick(PaymentAmountRadioState.MINIMUM_DUE, true);
        }
      }
    }
  }
}
